import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import Breadcrumbs from '../../components/breadcrumbs'
import Footer from '../../components/footer'
import DarkNav from '../../components/dark-nav'
import { getCanonicalUrl } from '../../utils/canonical'
import './monday.css'

const MONDAY_INSTALL_URL = 'https://auth.monday.com/oauth2/authorize?client_id=8ac87b4a5ef76bdbe8cfd86d65278935&response_type=install'

const MondayIntegration = () => {
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [openFaq, setOpenFaq] = useState(null)

  useEffect(() => {
    if (typeof AOS !== 'undefined') {
      AOS.init({ duration: 800, easing: 'ease-out', once: false, offset: 100, mirror: true })
    }
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)
      if (typeof AOS !== 'undefined') AOS.refresh()
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const canonicalUrl = getCanonicalUrl('/integrations/monday')

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations', path: '/integrations' },
    { label: 'monday.com' }
  ]

  const overview = [
    {
      name: 'Send SMS from any item',
      what: 'Build a Send SMS via DataFlows step into any monday automation recipe, triggered by a status change, a new item, or whatever fits your workflow.'
    },
    {
      name: 'Automated reminders',
      what: 'Text the right person ahead of a date on the board, such as an appointment, a deadline or a delivery, with no one clicking send.'
    },
    {
      name: 'Conditional SMS rules',
      what: 'Fire a text the moment a column changes, like a Status moving to "Ready for pickup", instead of watching a date.'
    },
    {
      name: 'Bulk SMS campaigns',
      what: 'Send to a filtered segment of a board at once, with delivery and failure tracking from the moment it sends.'
    },
    {
      name: 'AI reply handling',
      what: 'Every reply is classified by intent (confirm, cancel, reschedule, question or complaint) with a confidence score. You decide what happens next.'
    },
    {
      name: 'Shared SMS inbox',
      what: 'See every SMS conversation across every board in one place, each thread carrying its AI-classified priority.'
    },
    {
      name: 'Trackable links',
      what: 'Know when a link you texted actually gets clicked, without leaving monday.'
    },
    {
      name: 'Quiet hours & compliance',
      what: 'Account-wide quiet hours, rate limits and automatic opt-out handling, set once and applied everywhere.'
    },
  ]

  const setupSteps = [
    {
      title: 'Install the app',
      desc: <>Install DataFlows SMS on your monday.com account using our <a href={MONDAY_INSTALL_URL} target="_blank" rel="noreferrer noopener" className="monday-inline-link">install link</a>. You'll be asked to approve the permissions DataFlows needs: reading board data, writing back to columns you configure, posting Updates, and creating webhooks.</>
    },
    {
      title: 'Select your boards',
      desc: 'Choose the boards you want DataFlows to work with. You can connect more boards later from the same flow, and each installation can cover multiple boards.'
    },
    {
      title: 'You\'re connected',
      desc: 'DataFlows connects to monday.com over OAuth, so an installation stays linked to every board you selected with no re-authenticating per feature. Every feature is scoped per board, so you pick which boards use which features.'
    },
  ]

  const featureSections = [
    {
      id: 'send-sms',
      label: 'Automation block',
      title: 'Send One SMS from Any Item',
      intro: 'The fastest way to send a single SMS: build it into any monday automation recipe.',
      steps: [
        { title: 'Create an automation', desc: 'Open a board\'s Automations, click Create automation, and search "DataFlows".' },
        { title: 'Pick Send SMS via DataFlows', desc: 'Choose Send SMS via DataFlows from the DataFlows SMS app, then attach it to any trigger: a status change, a new item, whatever fits your workflow.' },
        { title: 'Fill in the sentence', desc: 'Choose which column holds the recipient\'s phone number, write your message (you can reference other columns), pick your Sender ID, and optionally a Status column to record delivery status right on the item.' },
      ],
      note: 'Replies to messages sent this way are what power the AI reply features and the Shared SMS Inbox further down this page. They\'re correlated automatically, so there is nothing extra to configure.',
    },
    {
      id: 'reminders',
      label: 'Reminder rules',
      title: 'Automated Reminders',
      intro: 'Text the right person automatically ahead of a date on the board, whether that\'s an appointment, a deadline or a delivery, with no one clicking send.',
      shot: {
        src: '/images/monday/img/reminders.png',
        alt: 'Reminder rules page showing a 24-Hour Appointment Reminder rule',
        caption: 'A reminder rule watching an Appointment Date column, firing 24 hours before.',
      },
      steps: [
        { title: 'Open Monday.com Reminder Rules', desc: 'From your DataFlows account, open Monday.com Reminder Rules and pick a connected board.' },
        { title: 'Choose the date and offset', desc: 'Choose the date column to watch and an offset: relative (for example 24 hours before) or a fixed time of day.' },
        { title: 'Add filters and business hours', desc: 'Optionally add a status filter so it only fires for items in the right stage, and set business-hours or weekday exclusion so nothing sends at 2am.' },
        { title: 'Write your message template', desc: 'Use {Column Title} tokens. They resolve against the item\'s real column values at send time.' },
        { title: 'Pick a sender and save', desc: 'Pick a sender from your connected numbers or Sender IDs, and save. The rule runs on its own from here.' },
      ],
    },
    {
      id: 'conditional',
      label: 'Conditional rules',
      title: 'Conditional SMS Rules',
      intro: 'Fire a text the moment a column changes (a Status moves to "Ready for pickup", a Dropdown flips to "Overdue") instead of watching a date.',
      steps: [
        { title: 'Open Monday.com Conditional SMS Rules', desc: 'Open Monday.com Conditional SMS Rules and pick a board.' },
        { title: 'Build your conditions', desc: 'Build one or more conditions against Status, Dropdown, Numbers or Checkbox columns, combined with ALL or ANY matching.' },
        { title: 'Write the message and pick a sender', desc: 'Write the message template and pick a sender. It uses the same token syntax as reminders.' },
      ],
      note: 'New rules never blast every item that already matches on day one. The first pass silently establishes a baseline, and only items that start matching after the rule exists will actually text someone.',
    },
    {
      id: 'campaigns',
      label: 'Bulk campaigns',
      title: 'Bulk SMS Campaigns',
      intro: 'Send to a filtered segment of a board at once, such as every overdue invoice or every lead at a certain stage, with delivery and failure tracking from the moment it sends.',
      shot: {
        src: '/images/monday/img/campaigns.png',
        alt: 'Bulk SMS campaigns page showing a Spring Service Reminder Blast with 248 recipients',
        caption: 'A campaign\'s recipient count, sent count and failed count, visible right on the list.',
      },
      steps: [
        { title: 'Choose a board and filter', desc: 'Open Bulk Campaigns, choose a board, and set your filter conditions. It\'s the same condition builder as conditional rules.' },
        { title: 'Preview before you send', desc: 'Preview the matching recipient count and your message template before anything sends.' },
        { title: 'Send', desc: 'Delivery runs in bounded waves, and quiet hours and your account\'s rate limit are respected automatically.' },
        { title: 'Track the results', desc: 'Check the campaign\'s status page for sent, failed and (if enabled) click counts.' },
      ],
    },
    {
      id: 'ai-replies',
      label: 'Reply-to-action rules',
      title: 'AI Replies & Automation',
      intro: 'Every reply to a DataFlows SMS is automatically classified by intent (confirm, cancel, reschedule, question or complaint) with a confidence score. You decide what happens next.',
      shot: {
        src: '/images/monday/img/reply-actions.png',
        alt: 'Reply-to-Action Rules page showing a rule that updates Status when a reply is confirmed at 75% confidence or higher',
        caption: 'A rule that updates a Status column automatically when a reply is classified "Confirm" at 75%+ confidence.',
      },
      steps: [
        { title: 'Open Monday.com Reply-to-Action Rules', desc: 'Open Monday.com Reply-to-Action Rules and pick a board.' },
        { title: 'Choose a trigger', desc: 'Match a specific intent at or above a confidence threshold, or catch anything the AI classifies with low confidence, which is a useful safety net for replies worth a human\'s attention.' },
        { title: 'Choose the action', desc: 'Update a status column, create a task, notify a teammate, or assign someone to the item.' },
      ],
      note: 'No rule set up yet? The classification still happens. You can expose it as its own board column any time from the same settings area, so intent and confidence are visible even without an automated action attached.',
    },
    {
      id: 'inbox',
      label: 'Shared SMS inbox',
      title: 'One Inbox for Every Board',
      intro: 'See every SMS conversation across every board in one place, something monday\'s own per-item Updates can\'t show you. Each thread carries its AI-classified priority.',
      shot: {
        src: '/images/monday/img/inbox.png',
        alt: 'Shared SMS Inbox showing an example conversation classified as Reschedule',
        caption: 'An example reply, classified "Reschedule" and linked back to the item it came from.',
      },
      steps: [
        { title: 'Open the Shared SMS Inbox', desc: 'Open Shared SMS Inbox from the header links on any of the Monday.com pages.' },
        { title: 'Pick a conversation', desc: 'See the full thread, its priority badge, and a live link back to the monday.com item it\'s tied to.' },
        { title: 'Reply from the inbox', desc: 'Your reply sends as a real SMS from the same number the conversation started on.' },
      ],
      note: 'The inbox is scoped to replies against the single-item Send SMS via DataFlows action for now. Reminders, conditional rules and campaigns aren\'t correlated into this view yet.',
    },
    {
      id: 'links',
      label: 'Trackable links',
      title: 'Trackable Links',
      intro: 'Know when a link you texted actually gets clicked (a quote, an invoice, a booking page) without leaving monday.',
      steps: [
        { title: 'Turn on Track link clicks', desc: 'On a reminder rule, conditional rule or campaign, turn on Track link clicks.' },
        { title: 'Pick a Text column', desc: 'Choose a Text column to hold the click status. DataFlows writes it automatically.' },
        { title: 'Watch the clicks come in', desc: 'Any link in your message is shortened and tracked. The column updates the moment someone taps it, showing a running click count.' },
      ],
    },
  ]

  const guardrails = [
    {
      name: 'Quiet hours',
      what: 'Set a sending window in Quiet Hours & Rate Limiting. Anything that would send outside it waits until the window reopens instead of sending late or early.'
    },
    {
      name: 'Rate limiting',
      what: 'Cap messages per hour or per day across the whole account. This is useful when a large campaign and your regular reminders run the same day.'
    },
    {
      name: 'Opt-outs',
      what: 'STOP, UNSUBSCRIBE and similar replies are handled automatically, platform-wide. An opted-out number is blocked from every future send, with no setup required.'
    },
    {
      name: 'Reporting',
      what: 'Response & Opt-Out Reports shows account-wide reply-time stats and opt-out activity, broken down by board.'
    },
  ]

  const requirements = [
    'An active DataFlows account',
    'A monday.com account with the boards you want to connect',
    'A column on each board that holds the recipient\'s phone number',
    'A connected number or Sender ID in DataFlows to send from',
  ]

  const faqItems = [
    {
      q: 'How does DataFlows connect to monday.com?',
      a: 'Over OAuth. You install the DataFlows SMS app, approve the permissions it needs (reading board data, writing back to columns you configure, posting Updates and creating webhooks), and select the boards to connect. The installation stays linked to those boards with no re-authenticating per feature.'
    },
    {
      q: 'Do I have to use every feature on every board?',
      a: 'No. Reminders, conditional rules, campaigns and reply automation are all scoped per board, so you choose which boards use which features.'
    },
    {
      q: 'Will a new conditional rule text everything that already matches?',
      a: 'No. The first pass silently establishes a baseline, so only items that start matching after the rule exists will actually text someone.'
    },
    {
      q: 'What happens when a customer replies STOP?',
      a: 'STOP, UNSUBSCRIBE and similar replies are handled automatically across the whole platform. An opted-out number is blocked from every future send, with no setup required.'
    },
    {
      q: 'Which replies show up in the Shared SMS Inbox?',
      a: 'For now, replies to messages sent with the single-item Send SMS via DataFlows action. Reminders, conditional rules and campaigns aren\'t correlated into the inbox view yet, although the AI classification of replies still works.'
    },
    {
      q: 'Can I stop messages sending at night?',
      a: 'Yes. Set a sending window in Quiet Hours & Rate Limiting and anything that would send outside it waits until the window reopens. Individual reminder rules can also apply business-hours or weekday exclusions.'
    },
    {
      q: 'What if I need help?',
      a: 'Email support@dataflows.com.au and our team will get back to you.'
    },
  ]

  const renderTable = (rows, heading) => (
    <table className="monday-feature-table">
      <thead>
        <tr>
          <th>{heading}</th>
          <th>What happens</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((f, i) => (
          <tr key={i}>
            <td>{f.name}</td>
            <td>{f.what}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )

  return (
    <div className="monday-container">
      <Helmet>
        <title>monday.com SMS Integration — Setup Guide | DataFlows</title>
        <meta name="description" content="Send SMS from monday.com boards with DataFlows: reminders, conditional rules, bulk campaigns, AI-classified replies, a shared inbox and trackable links." />
        <meta name="keywords" content="monday.com SMS, monday.com SMS integration, monday.com text message automation, monday.com SMS reminders, monday.com bulk SMS, DataFlows monday.com, monday.com two-way SMS, monday.com SMS Australia" />
        <meta property="og:title" content="monday.com SMS Integration — Setup Guide | DataFlows" />
        <meta property="og:description" content="Connect monday.com to DataFlows: send SMS from any item, automate reminders and conditional rules, run bulk campaigns, and handle replies with AI classification and a shared inbox." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "DataFlows SMS for monday.com",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "monday.com",
          "description": "Send SMS from monday.com boards: automation blocks, date-based reminders, conditional rules, bulk campaigns, AI-classified replies, a shared SMS inbox and trackable links.",
          "author": { "@type": "Organization", "name": "DataFlows SMS", "url": "https://dataflows.com.au" }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dataflows.com.au/" },
            { "@type": "ListItem", "position": 2, "name": "Integrations", "item": "https://dataflows.com.au/integrations" },
            { "@type": "ListItem", "position": 3, "name": "monday.com", "item": canonicalUrl }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      <DarkNav />

      <div className="monday-breadcrumbs">
        <div className="monday-inner">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      {/* Hero */}
      <section className="monday-hero" data-aos="fade-up">
        <div className="monday-inner">
          <div className="monday-hero-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="18" rx="1.5"/><rect x="14" y="3" width="7" height="11" rx="1.5"/></svg>
            monday.com Integration
          </div>
          <h1 className="monday-hero-title">DataFlows + monday.com</h1>
          <p className="monday-hero-subtitle">
            Send SMS straight from your monday.com boards. Build it into any automation, set up date-based reminders
            and conditional rules, run bulk campaigns, and handle customer replies with AI classification and a
            shared inbox, all driven by the data already on your boards.
          </p>
          <div className="monday-hero-actions">
            <a
              href="https://sms.dataflows.com.au/register"
              target="_blank"
              rel="noreferrer noopener"
              className="monday-btn-primary"
            >
              Get started
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
            </a>
            <a
              href={MONDAY_INSTALL_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="monday-btn-outline"
            >
              Install on monday.com
            </a>
            <a href="#setup" className="monday-btn-outline">
              How to connect
            </a>
          </div>
          <div className="monday-hero-trust">
            <span>✓ Connects over OAuth</span>
            <span>✓ Configured per board</span>
            <span>✓ Opt-outs handled automatically</span>
            <span>✓ Quiet hours &amp; rate limits</span>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="monday-section" id="features" data-aos="fade-up">
        <div className="monday-inner">
          <div className="monday-section-header">
            <span className="monday-label">Everything in one place</span>
            <h2>Everything Your Boards Need to Text</h2>
            <p>From a single SMS to a full campaign, and from a customer reply to an updated board item, DataFlows covers the whole conversation without leaving monday.com.</p>
          </div>
          <div className="monday-capability-grid">
            {overview.map((f, i) => (
              <div key={i} className="monday-capability" data-aos="fade-up" data-aos-delay={(i % 4) * 60}>
                <h3>{f.name}</h3>
                <p>{f.what}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Setup */}
      <section className="monday-section monday-section--dark" id="setup" data-aos="fade-up">
        <div className="monday-inner">
          <div className="monday-section-header">
            <span className="monday-label">Install</span>
            <h2>Connect Your Boards</h2>
            <p>Three steps from install to a connected board. Everything after this is configured inside your DataFlows account.</p>
          </div>
          <div className="monday-steps">
            {setupSteps.map((s, i) => (
              <div key={i} className="monday-step" data-aos="fade-up" data-aos-delay={i * 60}>
                <div className="monday-step-num">{i + 1}</div>
                <div className="monday-step-content">
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature walkthroughs */}
      {featureSections.map((sec, si) => (
        <section
          key={sec.id}
          id={sec.id}
          className={`monday-section${si % 2 === 0 ? '' : ' monday-section--dark'}`}
          data-aos="fade-up"
        >
          <div className="monday-inner">
            <div className="monday-section-header">
              <span className="monday-label">{sec.label}</span>
              <h2>{sec.title}</h2>
              <p>{sec.intro}</p>
            </div>
            {sec.shot && (
              <figure className="monday-shot">
                <img src={sec.shot.src} alt={sec.shot.alt} loading="lazy" />
                <figcaption>{sec.shot.caption}</figcaption>
              </figure>
            )}
            <div className="monday-steps" style={sec.shot ? { marginTop: '32px' } : undefined}>
              {sec.steps.map((s, i) => (
                <div key={i} className="monday-step">
                  <div className="monday-step-num">{i + 1}</div>
                  <div className="monday-step-content">
                    <h3>{s.title}</h3>
                    <p>{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            {sec.note && <div className="monday-callout">{sec.note}</div>}
          </div>
        </section>
      ))}

      {/* Guardrails */}
      <section
        className={`monday-section${featureSections.length % 2 === 0 ? '' : ' monday-section--dark'}`}
        id="account"
        data-aos="fade-up"
      >
        <div className="monday-inner">
          <div className="monday-section-header">
            <span className="monday-label">Account settings</span>
            <h2>Quiet Hours &amp; Compliance</h2>
            <p>Account-wide guardrails that apply across every reminder, conditional rule and campaign. Set them once.</p>
          </div>
          {renderTable(guardrails, 'Setting')}
        </div>
      </section>

      {/* Requirements */}
      <section
        className={`monday-section${(featureSections.length + 1) % 2 === 0 ? '' : ' monday-section--dark'}`}
        id="requirements"
        data-aos="fade-up"
      >
        <div className="monday-inner">
          <div className="monday-section-header">
            <span className="monday-label">Requirements</span>
            <h2>What You Need</h2>
          </div>
          <div className="monday-requirements">
            {requirements.map((r, i) => (
              <div key={i} className="monday-requirement">
                <span className="monday-requirement-icon">✓</span>
                <span>{r}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className={`monday-section${(featureSections.length + 2) % 2 === 0 ? '' : ' monday-section--dark'}`}
        id="faq"
        data-aos="fade-up"
      >
        <div className="monday-inner">
          <div className="monday-section-header">
            <span className="monday-label">FAQ</span>
            <h2>Common Questions</h2>
          </div>
          <div className="monday-faq">
            {faqItems.map((item, i) => (
              <div key={i} className={`monday-faq-item${openFaq === i ? ' open' : ''}`}>
                <button className="monday-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {item.q}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                </button>
                <p className={`monday-faq-a${openFaq === i ? '' : ' monday-faq-a--hidden'}`}>{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="monday-section" data-aos="fade-up">
        <div className="monday-inner">
          <h2 className="monday-related-title">Related Integrations</h2>
          <div className="monday-related-links">
            <Link to="/integrations/zapier" className="monday-related-link">Zapier SMS</Link>
            <Link to="/integrations/power-automate" className="monday-related-link">Power Automate SMS</Link>
            <Link to="/integrations/gohighlevel" className="monday-related-link">GoHighLevel SMS</Link>
            <Link to="/integrations/microsoft-teams" className="monday-related-link">Microsoft Teams SMS</Link>
            <Link to="/integrations/email-to-sms" className="monday-related-link">Email to SMS</Link>
            <span className="monday-related-link">monday.com SMS Automation</span>
            <span className="monday-related-link">monday.com SMS Reminders</span>
            <span className="monday-related-link">Two-Way SMS for monday.com</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="monday-cta" data-aos="fade-up">
        <div className="monday-inner">
          <h2>Ready to Connect?</h2>
          <p>Connect DataFlows to monday.com and start texting from your boards.</p>
          <div className="monday-cta-buttons">
            <a
              href="https://sms.dataflows.com.au/register"
              target="_blank"
              rel="noreferrer noopener"
              className="monday-btn-primary"
            >
              Get started
            </a>
            <a
              href={MONDAY_INSTALL_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="monday-btn-outline"
            >
              Install on monday.com
            </a>
            <a href="mailto:support@dataflows.com.au" className="monday-btn-outline">
              Talk to support
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {showScrollTop && (
        <div className="monday-scroll-top show" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <svg viewBox="0 0 24 24" fill="none"><path d="M12 5L19 12H16V19H8V12H5L12 5Z" fill="currentColor"/></svg>
        </div>
      )}
    </div>
  )
}

export default MondayIntegration
